/**
 * RFC 2047 encoded-word decoding.
 *
 * There is no decoder anywhere in this repo today, which is why `from:` cannot
 * find a German or Vietnamese sender by the name a human sees: the header on
 * disk reads `=?utf-8?Q?M=C3=BCller?=` and every predicate matches the raw
 * bytes. The address side of S2 stores the DECODED name, so this file is the
 * tripwire that whole feature hangs on.
 *
 * Two deliberate rules here:
 *
 * 1. NEVER THROW. A header is data that arrived from the internet, not a query
 *    the user typed. `parse.ts` throws QueryError on a bad query because the
 *    user can fix it; nobody can fix a 2003 mail server's broken base64. Any
 *    word we cannot decode is emitted verbatim, so the worst case is the
 *    status quo (the raw encoded word, which is what ships today).
 *
 * 2. DECODE AFTER SPLITTING, NEVER BEFORE. `parseAddressList` splits on commas
 *    first and calls `displayName` (hence this) per mailbox. That order is
 *    load-bearing: `=?utf-8?Q?Smith=2C_John?=` hides its comma inside the
 *    encoding, and base64's alphabet (A-Za-z0-9+/=) cannot contain one at all,
 *    so an undecoded list is always safe to split and a decoded one is not.
 */

/**
 * `=?charset?encoding?encoded-text?=`.
 *
 * RFC 2047 §2 forbids `?` inside encoded-text (Q spells it `=3F`, and base64's
 * alphabet has no `?`), so `[^?]*` is an exact terminator rather than a guess —
 * which is what stops the scan running past one word into the next. The charset
 * excludes whitespace so a stray `=?` in prose cannot swallow a line of text.
 */
const ENCODED_WORD = /=\?([^?\s]+)\?([QqBb])\?([^?]*)\?=/g

/** RFC 2047 §6.2 calls this linear-white-space; folding makes CR/LF part of it. */
const WHITESPACE_ONLY = /^[ \t\r\n]+$/

const HEX = /^[0-9A-Fa-f]{2}$/

/**
 * Q encoding is quoted-printable with one extra rule: `_` is a space. That rule
 * exists because RFC 2047 forbids literal spaces inside an encoded word, and it
 * is the single most commonly mis-implemented part of the spec.
 */
function decodeQ(text: string): Uint8Array {
  const bytes: number[] = []
  let i = 0
  while (i < text.length) {
    const ch = text[i]!
    if (ch === '_') {
      bytes.push(0x20)
      i++
      continue
    }
    if (ch === '=' && HEX.test(text.slice(i + 1, i + 3))) {
      bytes.push(Number.parseInt(text.slice(i + 1, i + 3), 16))
      i += 3
      continue
    }
    // A literal char. Legal Q text is ASCII, but headers in the wild carry raw
    // 8-bit bytes that arrived here already decoded into characters; re-encode
    // them as UTF-8 so a utf-8-labelled word still round-trips.
    const code = text.codePointAt(i)!
    if (code < 0x80) {
      bytes.push(code)
      i++
    } else {
      for (const b of new TextEncoder().encode(String.fromCodePoint(code))) bytes.push(b)
      i += code > 0xffff ? 2 : 1
    }
  }
  return Uint8Array.from(bytes)
}

/** Returns null on anything atob rejects, which the caller turns into "leave it alone". */
function decodeB(text: string): Uint8Array | null {
  // Folded base64 sometimes carries whitespace it is not supposed to have.
  const clean = text.replace(/[\s]/g, '')
  try {
    const binary = atob(clean)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  } catch {
    return null
  }
}

/**
 * Unknown charset labels fall back to utf-8 rather than failing the word.
 * A mislabelled charset is far commoner in a 20-year archive than a genuinely
 * opaque one, and under utf-8 the ASCII run of the name still decodes correctly,
 * which is strictly more than the raw `=?...?=` gives the reader.
 *
 * Note that WHATWG maps `iso-8859-1` and `us-ascii` onto windows-1252 (verified:
 * `new TextDecoder('iso-8859-1').encoding` is `'windows-1252'`). That is the
 * behaviour mail clients want — byte 0x92 renders as a curly apostrophe, which
 * is what the sending Outlook meant.
 */
function decodeBytes(bytes: Uint8Array, label: string): string {
  try {
    return new TextDecoder(label).decode(bytes)
  } catch {
    return new TextDecoder('utf-8').decode(bytes)
  }
}

function decodeWord(charset: string, encoding: string, text: string): string | null {
  const bytes = encoding === 'Q' || encoding === 'q' ? decodeQ(text) : decodeB(text)
  if (bytes === null) return null
  // RFC 2231 §5 hangs a language tag off the charset: `=?utf-8*en?Q?...?=`.
  const label = charset.split('*')[0]!
  return decodeBytes(bytes, label)
}

/**
 * Decode every encoded word in a header value, leaving everything else — and
 * anything malformed — exactly as it arrived.
 */
export function decodeEncodedWords(raw: string): string {
  // The overwhelming majority of headers in this archive contain no encoded
  // word at all, and this check keeps them off the regex engine entirely.
  if (!raw.includes('=?')) return raw

  let out = ''
  let last = 0
  let previousWasWord = false
  ENCODED_WORD.lastIndex = 0
  for (let match = ENCODED_WORD.exec(raw); match !== null; match = ENCODED_WORD.exec(raw)) {
    const gap = raw.slice(last, match.index)
    // RFC 2047 §6.2: whitespace separating two ADJACENT encoded words is part
    // of the encoding, not the text, and is dropped. Whitespace between a word
    // and ordinary text is real — `Re: =?utf-8?Q?x?=` keeps its space.
    const isFold = previousWasWord && gap.length > 0 && WHITESPACE_ONLY.test(gap)
    if (!isFold) out += gap

    const decoded = decodeWord(match[1]!, match[2]!, match[3]!)
    if (decoded === null) {
      out += match[0]
      previousWasWord = false
    } else {
      out += decoded
      previousWasWord = true
    }
    last = match.index + match[0].length
  }
  return out + raw.slice(last)
}
